import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';

/**
 * Org Structure — Variant C: Drag-and-Drop Assignment (Spatial Canvas)
 *
 * Props:
 *   employees      — full employee list
 *   entries        — existing org_structure rows
 *   skillGroups    — [{ value, label }]  module names from metadata
 *   skillsets      — [{ value, label }]  skillset names from metadata
 *   empSkillsMap   — { empId: [skillGroupName, ...] }
 *   empSkillsetMap — { empId: [skillsetName, ...] }  (optional, may be empty)
 *   onChange       — callback fired after a successful save so parent can refetch
 */
export default function OrgDragDropAssign({
  employees = [],
  entries = [],
  skillGroups = [],
  skillsets = [],
  empSkillsMap = {},
  empSkillsetMap = {},
  onChange = () => {},
}) {
  /* Assignment type selector */
  const [assignType, setAssignType] = useState('direct'); // 'direct' | 'dotted' | 'project'

  /* Left rail (employees) filters */
  const [lName, setLName] = useState('');
  const [lDept, setLDept] = useState('');
  const [lGroup, setLGroup] = useState('');
  const [lSkillset, setLSkillset] = useState('');

  /* Right rail (managers) filters */
  const [rName, setRName] = useState('');
  const [rDept, setRDept] = useState('');
  const [rGroup, setRGroup] = useState('');
  const [rSkillset, setRSkillset] = useState('');

  /* Drag state + hover feedback */
  const [draggingEmpId, setDraggingEmpId] = useState(null);
  const [hoverMgrId, setHoverMgrId] = useState(null);
  const [toast, setToast] = useState(null);

  /* Local entries — mirrors prop, but we mutate locally after each drop
     so the screen doesn't flash/refresh. */
  const [localEntries, setLocalEntries] = useState(entries || []);
  useEffect(() => { setLocalEntries(entries || []); }, [entries]);

  /* Additional "Show" filter on the left rail (employees) */
  const [showFilter, setShowFilter] = useState('all'); // 'all' | 'nodirect' | 'nodotted' | 'noproject'

  /* Derived department list */
  const departments = useMemo(() => {
    const seen = new Set();
    employees.forEach(e => { if (e.department_name) seen.add(e.department_name); });
    return [...seen].sort();
  }, [employees]);

  /* Lookup: empId -> { direct, dotted, project } manager ids (or undefined) */
  const mgrsByEmp = useMemo(() => {
    const m = {};
    (localEntries || []).forEach(e => {
      m[e.employee_id] = {
        direct:  e.manager_id || null,
        dotted:  e.dotted_manager_id || null,
        project: e.project_manager_id || null,
      };
    });
    return m;
  }, [localEntries]);

  /* Active-only list */
  const isActive = (e) => {
    const s = String(e.status || 'Active').toUpperCase();
    return s === 'ACTIVE';
  };

  const matchesFilters = useCallback((emp, nameQ, dept, group, skillset) => {
    if (nameQ) {
      const q = nameQ.toLowerCase();
      const full = `${emp.first_name || ''} ${emp.last_name || ''} ${emp.emp_code || ''}`.toLowerCase();
      if (!full.includes(q)) return false;
    }
    if (dept && String(emp.department_name || '') !== dept) return false;
    if (group) {
      const mine = empSkillsMap[emp.id] || [];
      // Also accept employee.module (primary skill group on employee master)
      if (!mine.some(g => g.toLowerCase() === group.toLowerCase()) &&
          String(emp.module || '').toLowerCase() !== group.toLowerCase()) return false;
    }
    if (skillset) {
      const mineSs = empSkillsetMap[emp.id] || [];
      if (!mineSs.some(s => s.toLowerCase() === skillset.toLowerCase())) return false;
    }
    return true;
  }, [empSkillsMap, empSkillsetMap]);

  /* Total count of Active employees (before the per-column filters) */
  const totalActiveEmployees = useMemo(
    () => employees.filter(isActive).length,
    [employees]
  );

  const leftList = useMemo(() => employees.filter(e => {
    if (!isActive(e)) return false;
    if (!matchesFilters(e, lName, lDept, lGroup, lSkillset)) return false;
    if (showFilter !== 'all') {
      const m = mgrsByEmp[e.id] || {};
      if (showFilter === 'nodirect'  && m.direct)  return false;
      if (showFilter === 'nodotted'  && m.dotted)  return false;
      if (showFilter === 'noproject' && m.project) return false;
    }
    return true;
  }), [employees, lName, lDept, lGroup, lSkillset, matchesFilters, showFilter, mgrsByEmp]);

  const rightList = useMemo(() => employees.filter(e => {
    if (!isActive(e)) return false;
    return matchesFilters(e, rName, rDept, rGroup, rSkillset);
  }), [employees, rName, rDept, rGroup, rSkillset, matchesFilters]);

  /* ── Connectors derived from LOCAL entries (updated optimistically on drop) ── */
  const connections = useMemo(() => {
    const out = [];
    (localEntries || []).forEach(e => {
      if (e.manager_id)          out.push({ from: e.employee_id, to: e.manager_id,          type: 'direct'  });
      if (e.dotted_manager_id)   out.push({ from: e.employee_id, to: e.dotted_manager_id,   type: 'dotted'  });
      if (e.project_manager_id)  out.push({ from: e.employee_id, to: e.project_manager_id,  type: 'project' });
    });
    return out;
  }, [localEntries]);

  /* ── Refs & connector redraw ── */
  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [repaintTick, setRepaintTick] = useState(0);
  const repaint = useCallback(() => setRepaintTick(t => t + 1), []);

  useEffect(() => {
    window.addEventListener('resize', repaint);
    return () => window.removeEventListener('resize', repaint);
  }, [repaint]);

  /* Scroll listeners on both rails */
  useEffect(() => {
    const L = leftRef.current, R = rightRef.current;
    if (L) L.addEventListener('scroll', repaint);
    if (R) R.addEventListener('scroll', repaint);
    return () => {
      if (L) L.removeEventListener('scroll', repaint);
      if (R) R.removeEventListener('scroll', repaint);
    };
  }, [repaint]);

  /* ── Render connectors in the SVG ── */
  useEffect(() => {
    const svg = svgRef.current;
    const canvas = canvasRef.current;
    if (!svg || !canvas) return;
    const cr = canvas.getBoundingClientRect();
    svg.setAttribute('viewBox', `0 0 ${cr.width} ${cr.height}`);
    svg.innerHTML = '';
    const ns = 'http://www.w3.org/2000/svg';

    const color = (t) => ({ direct: '#00E0FF', dotted: '#BF5FFF', project: '#3B8FF4' }[t] || '#888');

    connections.forEach((c) => {
      // Find DOM nodes — if not in current filter view, skip
      const l = canvas.querySelector(`[data-role="emp-left"][data-id="${c.from}"]`);
      const r = canvas.querySelector(`[data-role="emp-right"][data-id="${c.to}"]`);
      if (!l || !r) return;
      const lr = l.getBoundingClientRect();
      const rr = r.getBoundingClientRect();
      const x1 = lr.right - cr.left;
      const y1 = lr.top + lr.height / 2 - cr.top;
      const x2 = rr.left - cr.left;
      const y2 = rr.top + rr.height / 2 - cr.top;
      const midX = (x1 + x2) / 2;

      const path = document.createElementNS(ns, 'path');
      path.setAttribute('d', `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`);
      path.setAttribute('stroke', color(c.type));
      path.setAttribute('stroke-width', 2);
      path.setAttribute('fill', 'none');
      path.setAttribute('opacity', 0.75);
      if (c.type === 'dotted') path.setAttribute('stroke-dasharray', '8 5');
      path.setAttribute('filter', `drop-shadow(0 0 6px ${color(c.type)})`);
      svg.appendChild(path);

      // Endpoint circles
      [ [x1, y1], [x2, y2] ].forEach(([cx, cy]) => {
        const circle = document.createElementNS(ns, 'circle');
        circle.setAttribute('cx', cx);
        circle.setAttribute('cy', cy);
        circle.setAttribute('r', 3.5);
        circle.setAttribute('fill', color(c.type));
        svg.appendChild(circle);
      });
    });
  }, [connections, leftList, rightList, repaintTick]);

  /* ── Save on drop ──
     Optimistically update local state so the connector appears instantly with
     no screen flicker. Backend is called silently; on failure we roll back the
     local change and show a red toast. */
  async function saveAssignment(empId, mgrId, type) {
    const empName = nameOf(empId);
    const mgrName = nameOf(mgrId);
    setToast({ text: `${empName} → ${mgrName} (${typeLabel(type)})`, kind: 'ok' });
    setTimeout(() => setToast(null), 1900);

    // Snapshot for rollback
    const prev = localEntries;
    // Apply change optimistically
    const col = type === 'dotted' ? 'dotted_manager_id'
              : type === 'project' ? 'project_manager_id'
              : 'manager_id';
    setLocalEntries(prevRows => {
      const idx = prevRows.findIndex(r => String(r.employee_id) === String(empId));
      if (idx >= 0) {
        const copy = prevRows.slice();
        copy[idx] = { ...copy[idx], [col]: mgrId };
        return copy;
      }
      return [
        ...prevRows,
        {
          id: '_local_' + Date.now(),
          employee_id: empId,
          manager_id:         type === 'direct'  ? mgrId : null,
          dotted_manager_id:  type === 'dotted'  ? mgrId : null,
          project_manager_id: type === 'project' ? mgrId : null,
        },
      ];
    });

    try {
      const payload = {
        entries: [{
          employee_id: empId,
          manager_id:        type === 'direct'  ? mgrId : undefined,
          dotted_manager_id: type === 'dotted'  ? mgrId : undefined,
          project_manager_id:type === 'project' ? mgrId : undefined,
        }],
        managerType: type,
      };
      const res = await apiFetch('/api/workflow/org-structure/bulk', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setLocalEntries(prev); // rollback
        setToast({ text: `Save failed: ${d.error || 'unknown error'}`, kind: 'err' });
        setTimeout(() => setToast(null), 3500);
        return;
      }
      // Do NOT call onChange() — would cause parent refetch and screen flicker.
      // Local state already reflects the new connection.
    } catch (e) {
      setLocalEntries(prev); // rollback
      setToast({ text: `Network error: ${e.message}`, kind: 'err' });
      setTimeout(() => setToast(null), 3500);
    }
  }

  /* Delete a specific manager assignment (direct / dotted / project) for an employee */
  async function deleteAssignment(empId, type) {
    const empName = nameOf(empId);
    setToast({ text: `Removed ${typeLabel(type)} for ${empName}`, kind: 'ok' });
    setTimeout(() => setToast(null), 1900);

    const prev = localEntries;
    const col = type === 'dotted' ? 'dotted_manager_id'
              : type === 'project' ? 'project_manager_id'
              : 'manager_id';
    setLocalEntries(prevRows => prevRows.map(r =>
      String(r.employee_id) === String(empId) ? { ...r, [col]: null } : r
    ));

    try {
      const payload = {
        entries: [{
          employee_id: empId,
          manager_id:         type === 'direct'  ? null : undefined,
          dotted_manager_id:  type === 'dotted'  ? null : undefined,
          project_manager_id: type === 'project' ? null : undefined,
        }],
        managerType: type,
      };
      const res = await apiFetch('/api/workflow/org-structure/bulk', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setLocalEntries(prev);
        setToast({ text: `Delete failed: ${d.error || 'unknown error'}`, kind: 'err' });
        setTimeout(() => setToast(null), 3500);
      }
    } catch (e) {
      setLocalEntries(prev);
      setToast({ text: `Network error: ${e.message}`, kind: 'err' });
      setTimeout(() => setToast(null), 3500);
    }
  }

  function nameOf(empId) {
    const e = employees.find(x => String(x.id) === String(empId));
    return e ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : empId;
  }

  function typeLabel(t) {
    return { direct: 'Direct Manager', dotted: 'Dotted Line', project: 'Project Manager' }[t] || t;
  }

  /* ============================================================= */
  const S = styles;
  return (
    <div style={S.root}>
      {/* ── Filter bar ── */}
      <div style={S.filterBar}>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>Employee Filter (Column 1)</div>
          <div style={S.chipRow}>
            <input style={S.chipInput} placeholder="Name / Code..." value={lName} onChange={e => setLName(e.target.value)} />
            <select style={S.chipInput} value={lDept} onChange={e => setLDept(e.target.value)}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select style={S.chipInput} value={lGroup} onChange={e => setLGroup(e.target.value)}>
              <option value="">All Skill Groups</option>
              {skillGroups.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
            <select style={S.chipInput} value={lSkillset} onChange={e => setLSkillset(e.target.value)}>
              <option value="">All Skillsets</option>
              {skillsets.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select style={S.chipInput} value={showFilter} onChange={e => setShowFilter(e.target.value)} title="Show employees missing a manager">
              <option value="all">All</option>
              <option value="nodirect">No Direct Manager</option>
              <option value="nodotted">No Dotted Line Manager</option>
              <option value="noproject">No Project Manager</option>
            </select>
          </div>
        </div>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>Manager Filter (Column 3)</div>
          <div style={S.chipRow}>
            <input style={S.chipInput} placeholder="Name / Code..." value={rName} onChange={e => setRName(e.target.value)} />
            <select style={S.chipInput} value={rDept} onChange={e => setRDept(e.target.value)}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select style={S.chipInput} value={rGroup} onChange={e => setRGroup(e.target.value)}>
              <option value="">All Skill Groups</option>
              {skillGroups.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
            <select style={S.chipInput} value={rSkillset} onChange={e => setRSkillset(e.target.value)}>
              <option value="">All Skillsets</option>
              {skillsets.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>Assignment Type</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              ['direct',  'Direct',  '#00E0FF'],
              ['dotted',  'Dotted',  '#BF5FFF'],
              ['project', 'Project', '#3B8FF4'],
            ].map(([k, lbl, col]) => (
              <button key={k} onClick={() => setAssignType(k)} style={{
                padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                border: `1.5px solid ${assignType === k ? col : 'rgba(255,255,255,0.12)'}`,
                background: assignType === k ? `${col}18` : 'rgba(255,255,255,0.02)',
                color: assignType === k ? col : '#888',
                cursor: 'pointer', letterSpacing: '0.3px',
              }}>{lbl}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={S.legend}>
        <span><span style={{ ...S.legendDot, background: '#00E0FF' }} />Direct Manager</span>
        <span><span style={{ ...S.legendDot, borderBottom: '2px dotted #BF5FFF', background: 'transparent', height: 0 }} />Dotted Line</span>
        <span><span style={{ ...S.legendDot, background: '#3B8FF4' }} />Project Manager</span>
        <span style={{ marginLeft: 'auto', color: '#64748B', fontSize: 11 }}>
          Drag an employee from the left onto a person on the right. Saves instantly.
        </span>
      </div>

      {/* ── Spatial canvas ── */}
      <div ref={canvasRef} style={S.canvas}>
        <svg ref={svgRef} style={S.svg} xmlns="http://www.w3.org/2000/svg"></svg>

        <div style={S.centralLabel}>Drag across the canvas to connect</div>

        {/* Left rail */}
        <div ref={leftRef} style={{ ...S.rail, left: 16 }}>
          <div style={S.railTitle}>
            <span>Employees</span>
            <span style={{ color: '#64748B' }}>
              {leftList.length === totalActiveEmployees
                ? leftList.length
                : `${leftList.length} of ${totalActiveEmployees}`}
            </span>
          </div>
          {leftList.length === 0 ? (
            <div style={S.empty}>No employees match the filters</div>
          ) : leftList.map(e => (
            <div
              key={e.id}
              data-role="emp-left"
              data-id={e.id}
              draggable
              onDragStart={ev => { ev.dataTransfer.setData('text/eid', e.id); setDraggingEmpId(e.id); }}
              onDragEnd={() => setDraggingEmpId(null)}
              style={{
                ...S.node,
                opacity: draggingEmpId === e.id ? 0.35 : 1,
                cursor: 'grab',
              }}
            >
              <EmpBubble emp={e} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={S.nodeName}>{e.first_name} {e.last_name}</div>
                <div style={S.nodeSub}>{e.emp_code} · {e.department_name || '—'} · {e.module || '—'}</div>
                {(() => {
                  const m = mgrsByEmp[e.id] || {};
                  const chips = [];
                  if (m.direct)  chips.push({ type: 'direct',  color: '#00E0FF', label: 'D' });
                  if (m.dotted)  chips.push({ type: 'dotted',  color: '#BF5FFF', label: 'T' });
                  if (m.project) chips.push({ type: 'project', color: '#3B8FF4', label: 'P' });
                  if (chips.length === 0) return null;
                  return (
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }} onMouseDown={ev => ev.stopPropagation()}>
                      {chips.map(c => (
                        <span
                          key={c.type}
                          title={`${typeLabel(c.type)}: ${nameOf(m[c.type])} — click × to remove`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 2,
                            fontSize: 9.5, fontWeight: 700, padding: '1px 3px 1px 5px',
                            borderRadius: 3, lineHeight: 1.4,
                            color: c.color,
                            background: c.color + '22',
                            border: '1px solid ' + c.color + '55',
                          }}>
                          {c.label}
                          <button
                            onClick={async (ev) => {
                              ev.stopPropagation();
                              const ok = await toast.confirm({
                                title: 'Remove assignment?',
                                body: `${typeLabel(c.type)} (${nameOf(m[c.type])}) will be removed from ${e.first_name} ${e.last_name}.`,
                                danger: true, confirmLabel: 'Remove',
                              });
                              if (ok) deleteAssignment(e.id, c.type);
                            }}
                            style={{
                              all: 'unset', cursor: 'pointer',
                              width: 12, height: 12, borderRadius: 6,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              marginLeft: 2, fontSize: 10, lineHeight: 1,
                              color: c.color, opacity: 0.85,
                            }}
                            onMouseEnter={(ev) => ev.currentTarget.style.background = c.color + '33'}
                            onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}
                            title="Remove this assignment"
                          >×</button>
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>

        {/* Right rail */}
        <div ref={rightRef} style={{ ...S.rail, right: 16 }}>
          <div style={S.railTitle}>
            <span>Potential Managers</span><span style={{ color: '#64748B' }}>{rightList.length}</span>
          </div>
          {rightList.length === 0 ? (
            <div style={S.empty}>No managers match the filters</div>
          ) : rightList.map(m => (
            <div
              key={m.id}
              data-role="emp-right"
              data-id={m.id}
              onDragOver={ev => { ev.preventDefault(); setHoverMgrId(m.id); }}
              onDragLeave={() => setHoverMgrId(null)}
              onDrop={ev => {
                ev.preventDefault();
                const eid = ev.dataTransfer.getData('text/eid');
                setHoverMgrId(null);
                if (!eid || String(eid) === String(m.id)) return;
                saveAssignment(eid, m.id, assignType);
              }}
              style={{
                ...S.node,
                borderColor: hoverMgrId === m.id ? '#FF00E5' : 'rgba(255,255,255,0.08)',
                background: hoverMgrId === m.id ? 'rgba(255,0,229,0.12)' : 'rgba(255,255,255,0.04)',
                boxShadow: hoverMgrId === m.id ? '0 0 24px rgba(255,0,229,0.25)' : 'none',
              }}
            >
              <EmpBubble emp={m} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={S.nodeName}>{m.first_name} {m.last_name}</div>
                <div style={S.nodeSub}>{m.emp_code} · {m.department_name || '—'} · {m.module || '—'}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Legend pill */}
        <div style={S.badges}>
          <span style={S.pill}><span style={{ ...S.dot, background: '#00E0FF' }} />Direct</span>
          <span style={S.pill}><span style={{ ...S.dot, background: '#BF5FFF' }} />Dotted</span>
          <span style={S.pill}><span style={{ ...S.dot, background: '#3B8FF4' }} />Project</span>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24,
          background: 'rgba(10,14,24,0.96)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8, padding: '10px 14px', fontSize: 12.5,
          color: '#E5E7EB', display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 10000,
        }}>
          <span style={{
            width: 16, height: 16, borderRadius: 8,
            background: toast.kind === 'err' ? '#EF4444' : '#10B981',
            color: 'white', fontSize: 11, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>{toast.kind === 'err' ? '✕' : '✓'}</span>
          {toast.text}
        </div>
      )}
    </div>
  );
}

/* ========== inline emp avatar ========== */
function EmpBubble({ emp }) {
  if (emp.photo_path) {
    return (
      <img
        src={emp.photo_path}
        alt=""
        style={{
          width: 32, height: 32, borderRadius: '50%', objectFit: 'cover',
          border: '1px solid rgba(255,255,255,0.12)', flexShrink: 0,
        }}
      />
    );
  }
  const initials = ((emp.first_name || '')[0] || '') + ((emp.last_name || '')[0] || '');
  let h = 0;
  const seed = `${emp.first_name || ''} ${emp.last_name || ''}`;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const h1 = h % 360, h2 = (h1 + 40) % 360;
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700, color: '#fff',
      background: `linear-gradient(135deg, hsl(${h1} 70% 55%), hsl(${h2} 70% 45%))`,
    }}>{initials || '?'}</div>
  );
}

/* ========== styles ========== */
const styles = {
  root: { display: 'flex', flexDirection: 'column', gap: 14 },
  filterBar: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, padding: '14px 16px',
    display: 'grid', gap: 14,
    gridTemplateColumns: '1fr 1fr auto',
    alignItems: 'end',
  },
  filterGroup: { minWidth: 0 },
  filterLabel: {
    fontSize: 11, color: '#94A3B8', textTransform: 'uppercase',
    letterSpacing: '0.5px', fontWeight: 600, marginBottom: 8,
  },
  chipRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chipInput: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 6, padding: '6px 10px',
    fontSize: 12.5, color: '#E5E7EB', minWidth: 110, outline: 'none',
  },
  legend: {
    display: 'flex', gap: 16, alignItems: 'center',
    fontSize: 11.5, color: '#94A3B8', padding: '2px 4px',
  },
  legendDot: {
    display: 'inline-block', width: 10, height: 3, borderRadius: 2,
    marginRight: 6, verticalAlign: 'middle',
  },
  canvas: {
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12,
    position: 'relative', overflow: 'hidden', minHeight: 640,
    background: `
      radial-gradient(circle at 20% 30%, rgba(0,224,255,0.06), transparent 40%),
      radial-gradient(circle at 80% 70%, rgba(191,95,255,0.06), transparent 40%),
      rgba(255,255,255,0.03)`,
    backgroundImage: `
      linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)`,
    backgroundSize: '24px 24px',
  },
  svg: { position: 'absolute', inset: 0, pointerEvents: 'none', width: '100%', height: '100%' },
  centralLabel: {
    position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
    fontSize: 10.5, color: '#94A3B8', letterSpacing: '0.6px',
    textTransform: 'uppercase', fontWeight: 600,
  },
  rail: {
    position: 'absolute', top: 16, bottom: 16, width: 280, padding: 12,
    overflowY: 'auto', background: 'rgba(10,14,24,0.92)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
    backdropFilter: 'blur(8px)',
  },
  railTitle: {
    fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.7px',
    color: '#94A3B8', marginBottom: 8, fontWeight: 700,
    display: 'flex', justifyContent: 'space-between',
  },
  node: {
    display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, marginBottom: 6,
    transition: 'all 0.15s ease',
  },
  nodeName: {
    fontSize: 12, fontWeight: 600, color: '#E5E7EB',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  nodeSub: { fontSize: 10, color: '#64748B' },
  badges: {
    position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
    display: 'flex', gap: 10, padding: '8px 14px',
    background: 'rgba(0,0,0,0.55)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 30, backdropFilter: 'blur(6px)', fontSize: 11, color: '#E5E7EB',
  },
  pill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '4px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.04)',
  },
  dot: { width: 8, height: 8, borderRadius: '50%' },
  empty: { padding: 20, textAlign: 'center', color: '#64748B', fontSize: 12 },
};
